import {
  getEnecoBeXapiSiteApiV1OrdersByOrderId,
  postEnecoBeXapiSiteApiV1OrdersByOrderIdSimulate,
} from '@eneco/dc-be/client/services.gen';
import { PromoDto } from '@eneco/dc-be/client/types.gen.js';
import usePublicDCBE from '@eneco/dc-be/hooks/usePublicDCBE';
import unwrapData from '@eneco/dc-be/utils/unwrapData';
import { useRouter } from 'next/router';
import { useFormContext } from 'react-hook-form';

import { OrderManagementFormSchema } from '../types/orderManagementSchema';
import { findMatchingProductCombination } from '../utils/findMatchingProductCombination';

export const useSelectedProducts = ({
  orderId,
}: {
  orderId?: string;
} = {}) => {
  const { watch } = useFormContext<OrderManagementFormSchema>();
  const { query } = useRouter();
  const boilerMaintenance = watch('boilerMaintenance') ?? query.boilerMaintenance;
  const serviceProposal = watch('serviceProposal') ?? query.serviceProposal;
  const selectedServices = { boilerMaintenance, serviceProposal };

  const hasSelectedServices = Boolean(
    (boilerMaintenance && boilerMaintenance !== 'none') || (serviceProposal && serviceProposal !== 'none'),
  );
  const hasSelectedServiceProposal = Boolean(serviceProposal && serviceProposal !== 'none');
  const hasSelectedMaintenance = Boolean(boilerMaintenance && boilerMaintenance !== 'none');

  const shouldFetchFromApi = !!orderId;
  const cacheKey = `/orders/${orderId}`;

  const {
    data: orderDataRaw,
    isLoading: isLoadingOrder,
    error: orderDataError,
  } = usePublicDCBE(
    getEnecoBeXapiSiteApiV1OrdersByOrderId,
    { path: { orderId: orderId ?? '' } },
    [cacheKey],
    shouldFetchFromApi,
  );

  const orderData = shouldFetchFromApi ? unwrapData(orderDataRaw) : undefined;
  const hasOrderCommodityProducts =
    (orderData?.basket?.items?.filter(item => item?.orderProductType === 'Commodity')?.length ?? 0) > 0;
  const shouldFetchSimulate = shouldFetchFromApi && hasOrderCommodityProducts;

  const {
    data: orderSimulateRaw,
    isLoading: isLoadingSimulate,
    error: orderSimulateError,
  } = usePublicDCBE(
    postEnecoBeXapiSiteApiV1OrdersByOrderIdSimulate,
    { path: { orderId: orderId ?? '' } },
    [`${cacheKey}/simulate`],
    shouldFetchSimulate,
  );

  const orderSimulateResult = shouldFetchFromApi ? unwrapData(orderSimulateRaw) : undefined;

  if (shouldFetchFromApi && (orderSimulateResult?.productCombinationOptions ?? []).length > 0) {
    if (
      orderData &&
      orderSimulateResult?.productCombinationOptions[0] &&
      orderSimulateResult.productCombinationOptions[0]?.promoDisclaimers?.length > 0
    ) {
      const promoDisclaimer = orderSimulateResult.productCombinationOptions[0]?.promoDisclaimers?.[0]?.disclaimerText;
      const promoText = orderSimulateResult.productCombinationOptions[0]?.promoTexts?.[0];

      const promoInfo: PromoDto[] = (orderData.promos ?? []).map(promo => ({
        ...promo,
        offerDisclaimerNl: promoDisclaimer,
        offerDisclaimerFr: promoDisclaimer,
        offerTextNl: promoText,
        offerTextFr: promoText,
      }));

      orderData.promos = promoInfo;
    }
  }

  const combinationOptions = orderSimulateResult?.productCombinationOptions ?? [];
  const orderedCommodityProductNames =
    orderData?.basket?.items
      ?.filter(item => item?.orderProductType === 'Commodity')
      .map(item => item?.technicalProductName)
      .filter((name): name is string => Boolean(name)) ?? [];
  const matchedCombination = findMatchingProductCombination(combinationOptions, orderedCommodityProductNames);

  const hasElectricity = !!matchedCombination?.electricityProduct?.productName;
  const hasGas = !!matchedCombination?.gasProduct?.productName;
  const isBundle = !!(
    matchedCombination?.electricityProduct?.productName && matchedCombination?.gasProduct?.productName
  );
  const orderPromos = shouldFetchFromApi ? orderData?.promos : undefined;
  const isWelcomePromo = orderSimulateResult?.productCombinationOptions[0]?.isWelcomePromo ?? false;

  return {
    selectedProducts: matchedCombination,
    selectedServices,
    isBundle,
    hasSelectedProducts: hasElectricity || hasGas,
    hasSelectedElectricityProduct: hasElectricity,
    hasSelectedGasProduct: hasGas,
    hasSelectedServices,
    hasSelectedMaintenance,
    hasSelectedServiceProposal,
    cacheKey,
    orderPromos: orderPromos,
    isWelcomePromo,
    isLoading: isLoadingOrder || isLoadingSimulate,
    orderSimulateError,
    orderDataError,
  };
};
